import { computed, Injectable, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ConfirmDialogOptions } from './confirm-dialog.component';

/**
 * App-wide confirm dialog. Replaces blocking `window.confirm()` prompts with a
 * styled `sd-modal`: a single host (rendered at the app root) shows the dialog
 * whenever `options()` is set, and `respond()` resolves the observable returned
 * by `confirm()`. Works from anywhere — including route `CanDeactivate` guards,
 * which can return the observable directly.
 */
@Injectable({ providedIn: 'root' })
export class ConfirmDialogService {
  private readonly state = signal<ConfirmDialogOptions | null>(null);
  /** The active request's options, or null when no dialog is open. */
  public readonly options = computed(() => this.state());

  private result: Subject<boolean> | null = null;

  /** Open the dialog; the observable emits the user's choice once, then completes. */
  public confirm(options: ConfirmDialogOptions): Observable<boolean> {
    // Resolve any in-flight request as cancelled before replacing it.
    this.settle(false);
    this.result = new Subject<boolean>();
    this.state.set(options);
    return this.result.asObservable();
  }

  /** Called by the host when the user picks confirm (`true`) or cancel (`false`). */
  public respond(confirmed: boolean): void {
    this.settle(confirmed);
  }

  private settle(confirmed: boolean): void {
    const result = this.result;
    if (!result) {
      return;
    }
    this.result = null;
    this.state.set(null);
    result.next(confirmed);
    result.complete();
  }
}
